import type { Infer } from 'superstruct';
import {
	array,
	assign,
	enums,
	literal,
	nullable,
	number,
	object,
	omit,
	optional,
	partial,
	pick,
	record,
	string,
	union,
} from 'superstruct';

import {
	FacebookCustomAudienceItemDto,
	UploadFacebookTemplateJobReport,
	ViewFacebookTemplateDto,
} from '@adflow/shared/data-facebook-ads';
import { stringDate } from '@adflow/shared/util-superstruct';
import { ViewUserDto } from '@adflow/shared/util-users';

import { historyArrayItem } from '../history-array-item';
import type { HistoryEntityType } from '../history-types';

import { HistoryFacebookAdAccountDto } from './history-facebook-account.dto';
import { HistoryFacebookCampaignDto } from './history-facebook-campaign.dto';

export const HistoryFacebookTemplateDto = assign(
	partial(omit(ViewFacebookTemplateDto, ['updatedAt'])),
	partial(
		object({
			adAccounts: array(historyArrayItem(HistoryFacebookAdAccountDto)),
			campaignTemplates: array(historyArrayItem(HistoryFacebookCampaignDto)),
			customAudiences: record(
				string(),
				partial(
					object({
						included: array(historyArrayItem(FacebookCustomAudienceItemDto)),
						excluded: array(historyArrayItem(FacebookCustomAudienceItemDto)),
					})
				)
			),
		})
	)
);
export type HistoryFacebookTemplateDto = Infer<
	typeof HistoryFacebookTemplateDto
>;

const ViewHistoryFacebookTemplateBaseDto = object({
	id: number(),
	entityId: number(),
	user: pick(ViewUserDto, ['id', 'name', 'email']),
	userId: number(),
	createdAt: stringDate(),
	origin: optional(enums(['USER', 'SALESFORCE'])),
});

export const ViewHistoryFacebookTemplateUploadDto = assign(
	ViewHistoryFacebookTemplateBaseDto,
	object({
		entityType: literal('FacebookTemplate' satisfies HistoryEntityType),
		action: literal('UPLOAD'),
		oldData: optional(nullable(literal(null))),
		newData: UploadFacebookTemplateJobReport,
	})
);

const ViewHistoryFacebookTemplateOtherDto = assign(
	ViewHistoryFacebookTemplateBaseDto,
	object({
		entityType: literal('FacebookTemplate' satisfies HistoryEntityType),
		action: enums(['CREATE', 'UPDATE', 'DELETE']),
		oldData: nullable(optional(HistoryFacebookTemplateDto)),
		newData: HistoryFacebookTemplateDto,
	})
);

export type ViewHistoryFacebookTemplateUploadDto = Infer<
	typeof ViewHistoryFacebookTemplateUploadDto
>;

export const ViewHistoryFacebookTemplateDto = union([
	ViewHistoryFacebookTemplateUploadDto,
	ViewHistoryFacebookTemplateOtherDto,
]);

export type ViewHistoryFacebookTemplateDto = Infer<
	typeof ViewHistoryFacebookTemplateDto
>;
